/**
 * Form Enhancements - Floating Labels
 *
 * Key features:
 * 1. Cleaner selector strings (no whitespace issues)
 * 2. Better wrapper detection
 */

class FormEnhancements {
	constructor() {
		this.enhancedFields = new WeakSet();
		this.init();
	}

	/**
	 * Initialize form enhancements
	 */
	init() {
		// Initial enhancement on DOM ready
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => {
				this.enhanceAllFields();
			});
		} else {
			this.enhanceAllFields();
		}

		// Re-run after short delay for autofill detection
		setTimeout(() => this.enhanceAllFields(), 300);

		// WooCommerce Blocks support
		document.addEventListener('wc-blocks_checkout_blocks_rendered', () => {
			this.enhanceAllFields();
		});

		// Gravity Forms AJAX support
		document.addEventListener('gform_post_render', () => {
			this.enhanceAllFields();
		});

		// jQuery event support for Gravity Forms
		if (typeof jQuery !== 'undefined') {
			jQuery(document).on('gform_post_render', () => {
				this.enhanceAllFields();
			});
		}

		// MutationObserver for dynamic forms
		this.observeDynamicForms();
	}

	/**
	 * Field selectors - kept clean without line breaks
	 */
	getFieldSelectors() {
		return [
			// Gravity Forms
			'.gform_wrapper input[type="text"]',
			'.gform_wrapper input[type="email"]',
			'.gform_wrapper input[type="tel"]',
			'.gform_wrapper input[type="url"]',
			'.gform_wrapper input[type="number"]',
			'.gform_wrapper input[type="date"]',
			'.gform_wrapper input[type="time"]',
			'.gform_wrapper input[type="password"]',
			'.gform_wrapper textarea',
			'.gform_wrapper select',
			// WooCommerce Blocks
			'.wc-block-components-text-input input',
			'.wc-block-components-textarea textarea',
			// WooCommerce Classic
			'.woocommerce-input-wrapper input',
			'.woocommerce-input-wrapper textarea',
			'.woocommerce-input-wrapper select',
			// Custom forms
			'.form-field input:not([type="submit"]):not([type="checkbox"]):not([type="radio"])',
			'.form-field textarea',
			'.form-field select',
			'.wf-field-wrapper input:not([type="submit"]):not([type="checkbox"]):not([type="radio"])',
			'.wf-field-wrapper textarea',
			'.wf-field-wrapper select',
			'.floating-label-form input:not([type="submit"]):not([type="checkbox"]):not([type="radio"])',
			'.floating-label-form textarea',
			'.floating-label-form select',
		];
	}

	/**
	 * Complex field wrapper selectors (immediate containers)
	 */
	getComplexWrapperSelectors() {
		return [
			'.name_first',
			'.name_last',
			'.name_middle',
			'.name_prefix',
			'.name_suffix',
			'.ginput_left',
			'.ginput_right',
			'.address_line_1',
			'.address_line_2',
			'.address_city',
			'.address_state',
			'.address_zip',
			'.address_country',
		];
	}

	/**
	 * Main field wrapper selectors
	 */
	getMainWrapperSelectors() {
		return [
			'.gfield',
			'.wc-block-components-text-input',
			'.wc-block-components-textarea',
			'.woocommerce-input-wrapper',
			'.form-field',
			'.wf-field-wrapper',
		];
	}

	/**
	 * Find and enhance all form fields
	 */
	enhanceAllFields() {
		const selector = this.getFieldSelectors().join(', ');
		const fields = document.querySelectorAll(selector);

		fields.forEach(field => this.enhanceField(field));
	}

	/**
	 * Find the appropriate wrapper for a field
	 */
	findWrapper(field) {
		// First, check for complex field container (more specific)
		const complexSelectors = this.getComplexWrapperSelectors();
		for (const selector of complexSelectors) {
			const wrapper = field.closest(selector);
			if (wrapper) {
				return wrapper;
			}
		}

		// Fall back to main field wrapper
		const mainSelectors = this.getMainWrapperSelectors();
		for (const selector of mainSelectors) {
			const wrapper = field.closest(selector);
			if (wrapper) {
				return wrapper;
			}
		}

		return null;
	}

	/**
	 * Enhance a single field
	 */
	enhanceField(field) {
		// Skip if already enhanced
		if (this.enhancedFields.has(field)) {
			return;
		}

		// Mark as enhanced
		this.enhancedFields.add(field);

		// Find wrapper element
		const wrapper = this.findWrapper(field);

		if (!wrapper) {
			return;
		}

		// Create toggle function for has-value class
		const toggleHasValue = () => {
			const hasValue = field.value && field.value.trim() !== '';
			wrapper.classList.toggle('has-value', hasValue);
		};

		// Set initial state
		toggleHasValue();

		// If field is already focused on load
		if (document.activeElement === field) {
			wrapper.classList.add('is-focused');
		}

		// Focus event
		field.addEventListener('focus', () => {
			wrapper.classList.add('is-focused');
		});

		// Blur event
		field.addEventListener('blur', () => {
			wrapper.classList.remove('is-focused');
			toggleHasValue();
		});

		// Input event (for live typing)
		field.addEventListener('input', toggleHasValue);

		// Change event (for select, date, etc.)
		field.addEventListener('change', toggleHasValue);

		// Autofill detection (WebKit browsers)
		field.addEventListener('animationstart', (e) => {
			if (e.animationName === 'onAutoFillStart') {
				wrapper.classList.add('has-value');
			}
		});

		// Autofill detection (alternative method)
		const checkAutofill = () => {
			try {
				if (field.matches(':-webkit-autofill')) {
					wrapper.classList.add('has-value');
				}
			} catch (e) {
				// Browser doesn't support :-webkit-autofill selector
			}
		};

		// Check after delays (browsers autofill at different times)
		setTimeout(checkAutofill, 100);
		setTimeout(checkAutofill, 500);
		setTimeout(checkAutofill, 1000);
	}

	/**
	 * Observe DOM for dynamically added forms
	 */
	observeDynamicForms() {
		const observer = new MutationObserver((mutations) => {
			let shouldEnhance = false;

			for (const mutation of mutations) {
				if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
					for (const node of mutation.addedNodes) {
						if (node.nodeType === 1) {
							// Check if added node contains form fields
							if (
									node.matches?.('form, .gform_wrapper, .woocommerce') ||
									node.querySelector?.('input, textarea, select')
							) {
								shouldEnhance = true;
								break;
							}
						}
					}
				}
				if (shouldEnhance) break;
			}

			if (shouldEnhance) {
				// Debounce: wait a bit for all mutations to settle
				setTimeout(() => this.enhanceAllFields(), 100);
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	/**
	 * Manual refresh - call this if you dynamically add forms
	 */
	refresh() {
		this.enhanceAllFields();
	}
}

// Create instance
const formEnhancements = new FormEnhancements();

// Expose for manual refresh
window.formEnhancements = formEnhancements;

export default formEnhancements;
export { FormEnhancements };
